import type {
  CustomField,
  CustomFieldObject,
  ListCustomFieldsQuery,
} from '@clockwise/clients/custom-fields';
import {
  CUSTOM_FIELD_OBJECT_LABELS,
  CUSTOM_FIELD_OBJECTS,
  CUSTOM_FIELD_TYPE_LABELS,
  getObjectFlags,
} from '@clockwise/clients/custom-fields';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Archive, ArrowDown, ArrowUp, ArrowUpDown, Filter, Plus, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CustomFieldFormSheet } from '@/components/custom/custom-field-form-sheet';
import { NoAccess } from '@/components/custom/no-access';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { customFieldsApi } from '@/lib/api';
import { useMe } from '@/lib/me';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['label', 'key', 'fieldType', 'status', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];
const DEFAULT_SORT_FIELD: SortField = 'label';
const PAGE_SIZE = 25;

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'archived', 'all']).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/custom-fields')({
  validateSearch: listSearch,
  component: CustomFieldsPage,
});

function CustomFieldsPage() {
  const { permissions, loading: meLoading } = useMe();
  const canEdit = useHasPermission('custom_fields.edit');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  if (!meLoading && !permissions.has('custom_fields.view')) {
    return <NoAccess permission="custom_fields.view" resource="Custom fields" />;
  }

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  const [editing, setEditing] = useState<CustomField | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListCustomFieldsQuery = {
    q: q.trim() || undefined,
    status: status === 'all' ? undefined : status,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.customFields.list(listQuery),
    queryFn: () => customFieldsApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => customFieldsApi.remove(id),
    onSuccess: () => {
      toast.success('Custom field archived');
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Archive failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => customFieldsApi.restore(id),
    onSuccess: () => {
      toast.success('Custom field restored');
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<CustomField>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Label',
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.label}
            {row.original.status === 'archived' ? (
              <span className="ml-2 text-xs text-muted-foreground">(archived)</span>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'key',
        header: 'Key',
        cell: ({ row }) => (
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.original.key}</code>
        ),
      },
      {
        accessorKey: 'fieldType',
        header: 'Type',
        cell: ({ row }) => CUSTOM_FIELD_TYPE_LABELS[row.original.fieldType],
      },
      {
        id: 'objects',
        header: 'Available on',
        enableSorting: false,
        cell: ({ row }) => {
          const enabled = CUSTOM_FIELD_OBJECTS.filter(
            (h) => getObjectFlags(row.original, h).enabled,
          );
          if (enabled.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {enabled.map((h) => (
                <ObjectBadge
                  key={h}
                  object={h}
                  required={getObjectFlags(row.original, h).required}
                />
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RequirePermission permission="custom_fields.edit">
              {row.original.status === 'active' ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Archive"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Archive "${row.original.label}"?`)) {
                      archiveMutation.mutate(row.original.id);
                    }
                  }}
                  disabled={archiveMutation.isPending}
                >
                  <Archive />
                </Button>
              ) : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Restore"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreMutation.mutate(row.original.id);
                  }}
                  disabled={restoreMutation.isPending}
                >
                  <Undo2 />
                </Button>
              )}
            </RequirePermission>
          </RowActions>
        ),
      },
    ],
    [archiveMutation, restoreMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader
        title="Custom fields"
        subtitle="Tenant-defined attributes that can be attached to users, customers, or engagements."
        count={total}
      >
        <RequirePermission permission="custom_fields.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            New field
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search label, key…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <StickyTableScroll>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load custom fields.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q
                    ? 'No custom fields match this search.'
                    : 'No custom fields yet. Click “New field”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className={canEdit ? 'cursor-pointer' : undefined}
                  onClick={() => {
                    if (!canEdit) return;
                    setEditing(r.original);
                    setSheetOpen(true);
                  }}
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <CustomFieldFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        field={editing}
      />
    </PageShell>
  );
}

function ObjectBadge({
  object,
  required,
}: {
  object: CustomFieldObject;
  required: boolean | undefined;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs">
      {CUSTOM_FIELD_OBJECT_LABELS[object]}
      {required ? <span className="text-destructive">*</span> : null}
    </span>
  );
}

const defaultCell: ColumnDef<CustomField>['cell'] = ({ getValue }) => String(getValue() ?? '');

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  return out;
}
